import * as React from "react";
import { PauseIcon, PlayIcon } from "../icons";
import { cn } from "../lib/cn";
import { getPlayerControlButtonClasses } from "../lib/shared-styles";

export type PlayPauseButtonSize = "xs" | "sm" | "md" | "player" | "lg" | "xl";

export type PlayPauseButtonProps = {
  playing: boolean;
  size?: PlayPauseButtonSize;
  color?: "black" | "white";
  disabled?: boolean;
  onClick?: () => void;
  dataContext?: string;
  className?: string;
} & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "onClick" | "disabled">;

function PlayPauseIcon({
  size,
  playing,
}: {
  size: PlayPauseButtonSize;
  playing: boolean;
}) {
  const iconClass = "fill-current";

  if (size === "xs")
    return playing ? (
      <PauseIcon className={cn("h-4 w-4", iconClass)} />
    ) : (
      <PlayIcon className={cn("ml-1 h-4 w-4", iconClass)} />
    );
  if (size === "sm")
    return playing ? (
      <PauseIcon className={cn("h-5 w-5", iconClass)} />
    ) : (
      <PlayIcon className={cn("ml-1 h-5 w-5", iconClass)} />
    );
  if (size === "player")
    return playing ? (
      <PauseIcon className={cn("size-full", iconClass)} />
    ) : (
      <PlayIcon className={cn("size-full", iconClass)} />
    );
  if (size === "lg")
    return playing ? (
      <PauseIcon className={cn("h-7 w-7", iconClass)} />
    ) : (
      <PlayIcon className={cn("ml-1 h-7 w-7", iconClass)} />
    );
  if (size === "xl")
    return playing ? (
      <PauseIcon className={cn("h-8 w-8", iconClass)} />
    ) : (
      <PlayIcon className={cn("ml-1 h-8 w-8", iconClass)} />
    );
  return playing ? (
    <PauseIcon className={cn("h-6 w-6", iconClass)} />
  ) : (
    <PlayIcon className={cn("ml-1 h-6 w-6", iconClass)} />
  );
}

function getTestId(dataContext: string | undefined, playing: boolean): string {
  if (dataContext === "drawer") {
    return playing ? "drawer-pause-btn" : "drawer-play-btn";
  }
  return playing ? "no-drawer-pause-btn" : "no-drawer-play-button-btn";
}

export const PlayPauseButton = React.forwardRef<
  HTMLButtonElement,
  PlayPauseButtonProps
>(
  (
    {
      playing,
      size = "md",
      color = "black",
      disabled = false,
      onClick,
      dataContext,
      className,
      ...rest
    },
    ref,
  ) => {
    const testId = getTestId(dataContext, playing);

    return (
      <button
        ref={ref}
        type="button"
        onClick={onClick}
        disabled={disabled}
        aria-label={playing ? "Pause" : "Play"}
        className={cn(
          "grid shrink-0 place-items-center",
          getPlayerControlButtonClasses({ color, disabled }),
          {
            "size-8.5": size === "xs",
            "size-[2.835rem]": size === "sm",
            "size-[54px]": size === "md",
            "size-12": size === "player",
            "size-16": size === "lg",
          },
          className,
        )}
        aria-disabled={disabled}
        data-testid={testId}
        data-context={dataContext}
        {...rest}
      >
        <PlayPauseIcon size={size} playing={playing} />
      </button>
    );
  },
);
PlayPauseButton.displayName = "PlayPauseButton";
